// frontend/src/features/novs/NOVSView.jsx
// NOVS — Viewport-locked light-mode redesign

"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play, Trash2, Brush as Broom, FileDown, ListChecks, Search,
  AppWindow, Eye, Plus, X, FileText, Timer, ChevronDown,
  Zap, Clock, CheckCircle2, AlertCircle, Loader2, LogOut,
  Upload, Terminal, Layers, Archive, Paperclip, Lock, KeyRound,
} from "lucide-react";

/* ═══ Palette (BA Property Tax) ═══ */
const C = {
  navy: "#222D50", slate: "#363E5E", blue: "#7E9BC9",
  gray: "#70768D", muted: "#8E96AD", ltGray: "#C2C5CE",
  hint: "#B8BFD0", offWhite: "#FAFCFF",
  bg: "#F5F6FA", card: "#FFFFFF", border: "#EEEFF4",
};

/* ═══ Status config ═══ */
const STS = {
  saved:         { color: "#3DA86B", bg: "rgba(61,168,107,.06)", icon: CheckCircle2, t: "Saved" },
  "in progress": { color: C.blue,   bg: "rgba(126,155,201,.06)", icon: Loader2,      t: "Running" },
  error:         { color: "#C75454", bg: "rgba(199,84,84,.06)",  icon: AlertCircle,  t: "Error" },
  skipped:       { color: "#C9A84C", bg: "rgba(201,168,76,.06)", icon: AlertCircle,  t: "Skipped" },
  pending:       { color: C.ltGray,  bg: "rgba(194,197,206,.04)",icon: Clock,        t: "Queued" },
};
const sts = (s) => STS[String(s || "").toLowerCase()] || STS.pending;
const sp = { type: "spring", stiffness: 300, damping: 26 };

/* ═══ Progress ring ═══ */
function Ring({ pct, size = 58, stroke = 4.5 }) {
  const r = (size - stroke) / 2, circ = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={C.border} strokeWidth={stroke} />
      <motion.circle cx={size/2} cy={size/2} r={r} fill="none"
        stroke={pct >= 100 ? "#3DA86B" : C.blue} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={circ} animate={{ strokeDashoffset: circ - (pct/100)*circ }}
        transition={{ duration: .5, ease: "easeOut" }} />
    </svg>
  );
}

/* ═══ CAD Chip (with hover tooltip) ═══ */
function CadChip({ c, disabled, slow, onClick }) {
  return (
    <div className="nv-chip-outer">
      <motion.button type="button" disabled={disabled} onClick={() => onClick(c)}
        whileHover={disabled ? {} : { y: -1 }} whileTap={disabled ? {} : { scale: .97 }}
        className="nv-chip" style={{ opacity: disabled ? .4 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
        <span className="nv-dot" style={{ background: disabled ? C.ltGray : slow ? "#C9A84C" : "#3DA86B" }} />
        {c.name.replace(/🟢|🟡|🚧|🔴/g, "").trim()}
        {slow && !disabled && <span className="nv-tag nv-tag-y">slow</span>}
        {disabled && <span className="nv-tag nv-tag-m">soon</span>}
      </motion.button>
      <div className="nv-chip-tip">
        <div className="nv-chip-tip-key">key: <strong>{c.key}</strong></div>
        {c.idType && <div className="nv-chip-tip-row">ID: {c.idType}</div>}
        {c.detail && <div className="nv-chip-tip-row">{c.detail}</div>}
      </div>
    </div>
  );
}

/* ═══ CAD Drawer ═══ */
function CadDrawer({ COMING_SOON, SLOW_COLLECTORS, regionGroups, collectorSearch, collectorSearchResults, onCollectorSearchChange, onClearSearch, onCollectorTileClick }) {
  const hasQ = collectorSearch.trim().length > 0;
  const groups = hasQ ? [] : regionGroups.filter((g) => g.collectors.length > 0);
  return (
    <div className="nv-cad-drawer">
      <div className="nv-cad-search">
        <Search size={13} color={C.muted} />
        <input placeholder="Search CADs…" value={collectorSearch} onChange={(e) => onCollectorSearchChange(e.target.value)} />
        {collectorSearch && <button onClick={onClearSearch} className="nv-x-sm"><X size={10} /></button>}
      </div>
      <div className="nv-cad-scroll">
        {hasQ ? (
          collectorSearchResults.length === 0 ? <p className="nv-mute">No matches</p>
          : <div className="nv-chip-wrap">{collectorSearchResults.map((c) => <CadChip key={c.key} c={c} disabled={COMING_SOON.has(c.key)} slow={SLOW_COLLECTORS.has(c.key)} onClick={onCollectorTileClick} />)}</div>
        ) : groups.map((g) => (
          <div key={g.region}>
            <div className="nv-region-lbl">{g.region}</div>
            <div className="nv-chip-wrap">{g.collectors.map((c) => <CadChip key={c.key} c={c} disabled={COMING_SOON.has(c.key)} slow={SLOW_COLLECTORS.has(c.key)} onClick={onCollectorTileClick} />)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══ MAIN VIEW ═══ */
export default function NOVSView(props) {
  const {
    savedCount, showCollectors, COMING_SOON, SLOW_COLLECTORS,
    regionGroups, collectorSearch, collectorSearchResults,
    collectorInput, accountInput, yearInput,
    isHcad, isCollin, isBexar, isTarrant,
    file, fileRef, queue, log, busy, error, progressPct,
    onToggleCollectors, onCollectorTileClick,
    onCollectorSearchChange, onClearSearch,
    onCollectorInputChange, onAccountInputChange, onYearInputChange,
    onAddSingle, onPreviewFile, onFileChange, onClearFile,
    onClearSaved, onClearCompleted, onClearQueue,
    onStart, onRemoveAt, onClearLog, onAppsClick, logBoxRef,
    logout,
    // Credential modal
    showCredModal, credUsername, credPassword, credRemember,
    onCredUsernameChange, onCredPasswordChange, onCredRememberChange,
    onCredSubmit, onCredCancel,
  } = props;

  /* ── Timer ── */
  const [sec, setSec] = useState(0);
  const [running, setRunning] = useState(false);
  const tick = useRef(null);
  const savedAtStart = useRef(0);
  const go = () => {
    if (tick.current) clearInterval(tick.current);
    savedAtStart.current = queue.filter(r => /saved/i.test(r.status)).length;
    setSec(0); setRunning(true);
    tick.current = setInterval(() => setSec(s => s + 1), 1000);
  };
  const stop = () => { if (tick.current) { clearInterval(tick.current); tick.current = null; } setRunning(false); };
  useEffect(() => () => stop(), []);
  useEffect(() => {
    if (!running) return;
    if (queue.length > 0 && queue.every(r => ["Saved","Error","Skipped"].includes(r.status))) stop();
    if (!busy && Number(progressPct) === 100) stop();
  }, [queue, busy, progressPct, running]);

  const saved = queue.filter(r => /saved/i.test(r.status)).length;
  const errors = queue.filter(r => /error/i.test(r.status)).length;
  const savedThisRun = saved - savedAtStart.current;
  const avg = useMemo(() => (!savedThisRun || !sec) ? null : sec / savedThisRun, [savedThisRun, sec]);
  const clock = useMemo(() => {
    const p = n => String(n).padStart(2, "0");
    return `${p(Math.floor(sec/3600))}:${p(Math.floor((sec%3600)/60))}:${p(sec%60)}`;
  }, [sec]);

  const handleStart = () => { go(); onStart(); };
  useEffect(() => {
    const fn = e => { if ((e.ctrlKey||e.metaKey) && e.key === "Enter" && !busy && queue.length > 0) handleStart(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [busy, queue.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // All NOVS CADs benefit from specifying a year — show the input whenever a CAD is selected
  const needsYear = !!(collectorInput && collectorInput.trim());

  /* ── Show/hide panels ── */
  const [logOpen, setLogOpen] = useState(false);

  return (
    <div className="nv">
      <style>{CSS}</style>

      {/* ═══ HEADER — matches SplashPage style ═══ */}
      <header className="nv-hdr">
        <div className="nv-hdr-l">
          <img src="/assets/balogo.png" alt="" className="nv-logo" />
          <span className="nv-brand">BA Property Tax</span>
          <span className="nv-sep" />
          <span className="nv-app-name">NOVS</span>
        </div>
        <div className="nv-hdr-r">
          <button className="nv-hdr-btn" onClick={onAppsClick}><AppWindow size={13} /> Apps</button>
          {logout && <button className="nv-hdr-btn nv-hdr-out" onClick={logout}><LogOut size={13} /></button>}
        </div>
      </header>

      {/* ═══ BODY — viewport locked ═══ */}
      <div className="nv-body">

        {/* ── LEFT RAIL: input + catalog ── */}
        <aside className="nv-rail">
          {/* Quick add */}
          <div className="nv-sec">
            <div className="nv-sec-lbl">Add account</div>
            <div className="nv-add-stack">
              <input className="nv-in" placeholder="CAD (dallas, collin…)" value={collectorInput} onChange={e => onCollectorInputChange(e.target.value)} />
              <input id="novs-account-input" className="nv-in nv-mono" placeholder="Account / PIN #" value={accountInput}
                onChange={e => onAccountInputChange(e.target.value)} onKeyDown={e => { if (e.key === "Enter") onAddSingle(); }} />
              {needsYear && <input className="nv-in nv-in-sm" placeholder="Year" inputMode="numeric" value={yearInput} onChange={e => onYearInputChange(e.target.value)} />}
              <button className="nv-add-btn" onClick={onAddSingle} disabled={busy}><Plus size={15} /></button>
            </div>
          </div>

          {/* Import */}
          <div className="nv-sec">
            <div className="nv-sec-lbl">Import spreadsheet</div>
            <div className="nv-import">
              <label className="nv-file-lbl">
                <Paperclip size={13} />
                <span>{file ? file.name : "Choose .xlsx / .csv"}</span>
                <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={e => onFileChange(e.target.files?.[0] || null)} hidden />
              </label>
              <button className="nv-sm-btn" onClick={onPreviewFile} disabled={busy || !file}><Eye size={12} /> Queue</button>
              {file && <button className="nv-x-btn" onClick={onClearFile}><X size={12} /></button>}
            </div>
          </div>

          {/* CAD catalog */}
          <div className="nv-sec nv-sec-grow">
            <button className="nv-sec-toggle" onClick={onToggleCollectors}>
              <Layers size={13} color={C.blue} />
              <span>CAD Catalog</span>
              <ChevronDown size={13} className={showCollectors ? "nv-flip" : ""} />
            </button>
            <AnimatePresence initial={false}>
              {showCollectors && (
                <motion.div key="drawer" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }} transition={sp} style={{ overflow: "hidden", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                  <CadDrawer COMING_SOON={COMING_SOON} SLOW_COLLECTORS={SLOW_COLLECTORS}
                    regionGroups={regionGroups} collectorSearch={collectorSearch}
                    collectorSearchResults={collectorSearchResults}
                    onCollectorSearchChange={onCollectorSearchChange}
                    onClearSearch={onClearSearch} onCollectorTileClick={onCollectorTileClick} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </aside>

        {/* ── CENTER: queue feed ── */}
        <main className="nv-center">
          {/* Stat bar */}
          <div className="nv-stat-bar">
            <div className="nv-ring-wrap">
              <Ring pct={progressPct} />
              <div className="nv-ring-lbl">
                <span className="nv-ring-pct">{progressPct}<small>%</small></span>
              </div>
            </div>

            <div className="nv-pills">
              <div className="nv-pill"><span className="nv-pill-n">{queue.length}</span><span className="nv-pill-l">queued</span></div>
              <div className="nv-pill-div" />
              <div className="nv-pill"><span className="nv-pill-n" style={{ color: "#3DA86B" }}>{saved}</span><span className="nv-pill-l">saved</span></div>
              <div className="nv-pill-div" />
              <div className="nv-pill"><span className="nv-pill-n" style={{ color: "#C75454" }}>{errors}</span><span className="nv-pill-l">errors</span></div>
              <div className="nv-pill-div" />
              <div className="nv-pill"><span className="nv-pill-n">{avg == null ? "—" : avg.toFixed(1) + "s"}</span><span className="nv-pill-l">avg/save</span></div>
              <div className="nv-pill-div" />
              <div className="nv-pill"><span className="nv-pill-n nv-mono-sm">{clock}</span><span className="nv-pill-l">elapsed</span></div>
            </div>

            <div className="nv-stat-right">
              <button className="nv-run" onClick={handleStart} disabled={busy || !queue.length}>
                {busy ? <Loader2 size={15} className="nv-spin" /> : <Play size={15} />}
                {busy ? "Running…" : "Start"}
                {!busy && <kbd className="nv-kbd">⌘↵</kbd>}
              </button>
            </div>
          </div>

          {/* Action strip */}
          <div className="nv-actions">
            <button className="nv-act" onClick={onClearSaved} disabled={busy || !saved}><ListChecks size={12} /> Clear saved</button>
            <button className="nv-act" onClick={onClearCompleted} disabled={busy}><Broom size={12} /> Clear done</button>
            <button className="nv-act" onClick={onClearQueue} disabled={busy || !queue.length}><Trash2 size={12} /> Clear all</button>
            <div style={{ flex: 1 }} />
            <button className={`nv-act ${logOpen ? "nv-act-on" : ""}`} onClick={() => setLogOpen(v => !v)}>
              <Terminal size={12} /> Log {log.length > 0 && <span className="nv-act-badge">{log.length}</span>}
            </button>
          </div>

          {error && <div className="nv-err"><AlertCircle size={13} /> {error}</div>}

          {/* Queue + optional log */}
          <div className={`nv-workspace ${logOpen ? "nv-ws-split" : ""}`}>
            <div className="nv-feed">
              <AnimatePresence mode="popLayout">
                {queue.length === 0 ? (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="nv-empty">
                    <Archive size={32} color={C.ltGray} strokeWidth={1.5} />
                    <p className="nv-empty-t">No accounts queued</p>
                    <p className="nv-mute">Add on the left or import a spreadsheet to get started</p>
                  </motion.div>
                ) : queue.map((r, i) => {
                  const s = sts(r.status);
                  const Icon = s.icon;
                  const isRun = r.status === "In Progress";
                  return (
                    <motion.div key={`${r.collector}:${r.account}:${r.year||""}`}
                      layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: .96 }} transition={sp}
                      className={`nv-row ${isRun ? "nv-row-active" : ""}`}>
                      <span className="nv-row-n">{i + 1}</span>
                      <span className="nv-row-cad">{r.collector}</span>
                      <span className="nv-row-acct">{r.account}{r.year ? ` · ${r.year}` : ""}</span>
                      <span className="nv-row-sts" style={{ color: s.color, background: s.bg }}>
                        <Icon size={12} className={isRun ? "nv-spin" : ""} /> {s.t}
                      </span>
                      {r.filename && <span className="nv-row-file">{r.filename}</span>}
                      <button className="nv-row-rm" onClick={() => onRemoveAt(i)} disabled={busy}><X size={11} /></button>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            {/* Log panel (inline, toggled) */}
            <AnimatePresence>
              {logOpen && (
                <motion.div key="log" initial={{ width: 0, opacity: 0 }} animate={{ width: 300, opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }} transition={sp} className="nv-log-panel">
                  <div className="nv-log-hdr">
                    <Terminal size={12} color={C.blue} /> <span>Log</span>
                    <button className="nv-x-btn" onClick={onClearLog} style={{ marginLeft: "auto" }}><Trash2 size={10} /></button>
                  </div>
                  <div ref={logBoxRef} className="nv-log-box">
                    {log.length === 0
                      ? <span className="nv-mute">Waiting for activity…</span>
                      : log.map((l, i) => <div key={i} className="nv-log-ln">{l}</div>)}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </main>
      </div>

      {/* ═══ CREDENTIAL MODAL ═══ */}
      <AnimatePresence>
        {showCredModal && (
          <motion.div
            key="cred-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="nv-cred-overlay"
            onClick={onCredCancel}
          >
            <motion.div
              key="cred-modal"
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={sp}
              className="nv-cred-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="nv-cred-icon-wrap">
                <Lock size={20} color={C.blue} />
              </div>
              <h3 className="nv-cred-title">Portal Login Required</h3>
              <p className="nv-cred-sub">
                Tarrant County requires login credentials to download appraisal notices.
              </p>

              <div className="nv-cred-fields">
                <label className="nv-cred-lbl">
                  <span>Email / Username</span>
                  <input
                    className="nv-in"
                    type="text"
                    autoComplete="username"
                    placeholder="user@example.com"
                    value={credUsername}
                    onChange={(e) => onCredUsernameChange(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && credUsername && credPassword) onCredSubmit(); }}
                    autoFocus
                  />
                </label>
                <label className="nv-cred-lbl">
                  <span>Password</span>
                  <input
                    className="nv-in"
                    type="password"
                    autoComplete="current-password"
                    placeholder="Password"
                    value={credPassword}
                    onChange={(e) => onCredPasswordChange(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && credUsername && credPassword) onCredSubmit(); }}
                  />
                </label>
                <label className="nv-cred-check">
                  <input
                    type="checkbox"
                    checked={credRemember}
                    onChange={(e) => onCredRememberChange(e.target.checked)}
                  />
                  <span>Remember credentials</span>
                </label>
              </div>

              <div className="nv-cred-actions">
                <button className="nv-cred-cancel" onClick={onCredCancel}>Cancel</button>
                <button
                  className="nv-cred-submit"
                  onClick={onCredSubmit}
                  disabled={!credUsername || !credPassword}
                >
                  <KeyRound size={13} /> Continue
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ═══════════════════════════════════════════ CSS ═══════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}

/* ── Root: viewport-locked ── */
.nv{
  height:100vh;overflow:hidden;
  display:flex;flex-direction:column;
  font-family:'Montserrat',-apple-system,BlinkMacSystemFont,sans-serif;
  background:${C.bg};color:${C.navy};
}

/* ── Header (SplashPage style) ── */
.nv-hdr{
  flex-shrink:0;display:flex;align-items:center;justify-content:space-between;
  padding:10px clamp(16px,3vw,32px);
  background:white;border-bottom:1px solid ${C.border};z-index:20;
}
.nv-hdr-l{display:flex;align-items:center;gap:8px;}
.nv-logo{height:24px;width:auto;}
.nv-brand{font-size:12px;font-weight:700;color:${C.navy};}
.nv-sep{width:1px;height:16px;background:${C.border};margin:0 4px;}
.nv-app-name{font-size:12px;font-weight:800;color:${C.blue};letter-spacing:.5px;}
.nv-hdr-r{display:flex;align-items:center;gap:6px;}
.nv-hdr-btn{
  display:inline-flex;align-items:center;gap:5px;
  padding:6px 12px;border-radius:8px;
  border:1px solid ${C.border};background:#F9FAFB;
  color:${C.muted};font-size:11px;font-weight:600;
  cursor:pointer;font-family:inherit;transition:all .2s;
}
.nv-hdr-btn:hover{border-color:#D8DBE6;background:white;box-shadow:0 2px 8px rgba(126,155,201,.06);}
.nv-hdr-out{padding:6px 8px;border-color:#F5D5D5;color:#C05050;}
.nv-hdr-out:hover{background:#FEF2F2;border-color:#E8B0B0;}

/* ── Body: 2-column viewport-locked ── */
.nv-body{
  flex:1;display:grid;grid-template-columns:300px 1fr;
  min-height:0;overflow:hidden;
}

/* ── Left rail ── */
.nv-rail{
  display:flex;flex-direction:column;gap:0;
  border-right:1px solid ${C.border};
  background:white;overflow-y:auto;overflow-x:hidden;
  padding:14px;
  scrollbar-width:thin;scrollbar-color:${C.border} transparent;
}
.nv-sec{margin-bottom:14px;}
.nv-sec-grow{flex:1;min-height:0;display:flex;flex-direction:column;}
.nv-sec-lbl{font-size:10px;font-weight:700;color:${C.muted};text-transform:uppercase;letter-spacing:.8px;margin-bottom:8px;}
.nv-sec-toggle{
  display:flex;align-items:center;gap:6px;width:100%;
  background:none;border:none;cursor:pointer;font-family:inherit;
  font-size:10px;font-weight:700;color:${C.muted};
  text-transform:uppercase;letter-spacing:.8px;
  padding:0;margin-bottom:6px;
}

/* ── Inputs ── */
.nv-add-stack{display:flex;flex-direction:column;gap:6px;}
.nv-in{
  width:100%;padding:8px 11px;border-radius:8px;
  border:1px solid ${C.border};background:${C.bg};
  color:${C.navy};font-size:12px;font-weight:500;font-family:inherit;
  outline:none;transition:all .2s;
}
.nv-in:focus{border-color:${C.blue};box-shadow:0 0 0 3px rgba(126,155,201,.08);}
.nv-in-sm{width:100%;}
.nv-mono{font-family:'Courier New',monospace;font-size:12px;letter-spacing:.3px;}
.nv-add-btn{
  width:100%;padding:9px;border-radius:8px;border:none;
  background:${C.navy};color:${C.offWhite};
  display:flex;align-items:center;justify-content:center;
  cursor:pointer;font-weight:700;font-size:13px;
  transition:all .15s;
}
.nv-add-btn:hover{background:${C.slate};}
.nv-add-btn:disabled{opacity:.35;cursor:not-allowed;}

/* ── Import ── */
.nv-import{display:flex;flex-direction:column;gap:6px;}
.nv-file-lbl{
  display:flex;align-items:center;gap:6px;
  padding:8px 11px;border-radius:8px;
  border:1px dashed rgba(126,155,201,.25);background:${C.bg};
  color:${C.gray};font-size:11px;font-weight:500;cursor:pointer;
  transition:all .2s;overflow:hidden;
}
.nv-file-lbl span{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.nv-file-lbl:hover{border-color:${C.blue};color:${C.navy};}
.nv-sm-btn{
  display:inline-flex;align-items:center;justify-content:center;gap:4px;
  padding:7px 12px;border-radius:7px;
  border:1px solid ${C.border};background:white;
  color:${C.navy};font-size:11px;font-weight:600;
  cursor:pointer;font-family:inherit;transition:all .15s;
}
.nv-sm-btn:hover{border-color:${C.blue};box-shadow:0 1px 4px rgba(126,155,201,.08);}
.nv-sm-btn:disabled{opacity:.35;cursor:not-allowed;}
.nv-x-btn{
  width:24px;height:24px;border-radius:6px;border:none;flex-shrink:0;
  background:rgba(34,45,80,.03);color:${C.ltGray};
  display:flex;align-items:center;justify-content:center;
  cursor:pointer;transition:all .15s;
}
.nv-x-btn:hover{background:rgba(199,84,84,.06);color:#C75454;}

/* ── CAD drawer ── */
.nv-cad-drawer{margin-top:4px;flex:1;display:flex;flex-direction:column;min-height:0;}
.nv-cad-search{
  display:flex;align-items:center;gap:6px;
  padding:7px 10px;border-radius:8px;
  background:${C.bg};border:1px solid ${C.border};margin-bottom:8px;
}
.nv-cad-search input{flex:1;border:none;background:none;outline:none;font-size:11px;color:${C.navy};font-family:inherit;font-weight:500;}
.nv-cad-search input::placeholder{color:${C.ltGray};}
.nv-x-sm{width:16px;height:16px;border-radius:8px;border:none;background:rgba(34,45,80,.05);color:${C.gray};display:flex;align-items:center;justify-content:center;cursor:pointer;}
.nv-cad-scroll{flex:1;overflow-y:auto;display:flex;flex-direction:column;gap:8px;scrollbar-width:thin;scrollbar-color:${C.border} transparent;}
.nv-region-lbl{font-size:9px;font-weight:800;color:${C.hint};text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;}
.nv-chip-wrap{display:flex;flex-wrap:wrap;gap:4px;margin-bottom:4px;}
.nv-chip-outer{position:relative;display:inline-flex;}
.nv-chip{
  display:inline-flex;align-items:center;gap:5px;
  padding:5px 10px;border-radius:7px;
  border:1px solid ${C.border};background:white;
  font-size:11px;font-weight:600;color:${C.navy};
  font-family:inherit;transition:all .15s;
}
.nv-chip:hover:not(:disabled){border-color:${C.blue};background:rgba(126,155,201,.03);}
.nv-dot{width:5px;height:5px;border-radius:50%;flex-shrink:0;}
.nv-tag{font-size:8px;font-weight:700;padding:1px 5px;border-radius:3px;text-transform:uppercase;letter-spacing:.3px;}
.nv-tag-y{color:#C9A84C;background:rgba(201,168,76,.1);}
.nv-tag-m{color:${C.ltGray};background:rgba(194,197,206,.1);}
.nv-flip{transform:rotate(180deg);transition:transform .25s;}

/* ── Chip tooltip ── */
.nv-chip-tip{
  display:none;position:absolute;left:0;top:100%;margin-top:4px;z-index:50;
  min-width:180px;max-width:240px;
  padding:8px 10px;border-radius:8px;
  background:${C.navy};color:${C.offWhite};
  font-size:10px;font-weight:500;line-height:1.5;
  box-shadow:0 6px 20px rgba(34,45,80,.25);
  pointer-events:none;
}
.nv-chip-outer:hover .nv-chip-tip{display:block;}
.nv-chip-tip-key{font-family:'Courier New',monospace;font-size:10px;color:${C.blue};margin-bottom:2px;}
.nv-chip-tip-key strong{color:${C.offWhite};font-weight:700;}
.nv-chip-tip-row{color:rgba(250,252,255,.75);}

/* ── CENTER ── */
.nv-center{
  display:flex;flex-direction:column;min-height:0;overflow:hidden;
  padding:12px clamp(12px,2vw,20px);gap:8px;
}

/* ── Stat bar ── */
.nv-stat-bar{
  flex-shrink:0;display:flex;align-items:center;gap:16px;
  background:white;border-radius:14px;border:1px solid ${C.border};
  padding:10px 16px;
}
.nv-ring-wrap{position:relative;flex-shrink:0;}
.nv-ring-lbl{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;}
.nv-ring-pct{font-size:14px;font-weight:900;color:${C.navy};}
.nv-ring-pct small{font-size:9px;font-weight:600;color:${C.muted};}
.nv-pills{flex:1;display:flex;align-items:center;gap:12px;}
.nv-pill{display:flex;flex-direction:column;align-items:center;}
.nv-pill-n{font-size:16px;font-weight:800;color:${C.navy};line-height:1;}
.nv-pill-l{font-size:8px;font-weight:600;color:${C.hint};text-transform:uppercase;letter-spacing:.5px;margin-top:1px;}
.nv-pill-div{width:1px;height:20px;background:${C.border};}
.nv-mono-sm{font-family:'Courier New',monospace;font-size:13px;letter-spacing:.5px;}

.nv-stat-right{flex-shrink:0;}
.nv-run{
  display:inline-flex;align-items:center;gap:6px;
  padding:9px 22px;border-radius:10px;border:none;
  background:${C.navy};color:${C.offWhite};
  font-size:12px;font-weight:700;cursor:pointer;font-family:inherit;
  box-shadow:0 2px 10px rgba(34,45,80,.15);transition:all .2s;
}
.nv-run:hover{background:${C.slate};box-shadow:0 4px 16px rgba(34,45,80,.2);}
.nv-run:disabled{opacity:.35;cursor:not-allowed;}
.nv-kbd{font-size:8px;font-weight:700;color:rgba(200,210,230,.5);background:rgba(126,155,201,.12);border-radius:3px;padding:1px 5px;margin-left:2px;}

/* ── Actions strip ── */
.nv-actions{flex-shrink:0;display:flex;align-items:center;gap:4px;}
.nv-act{
  display:inline-flex;align-items:center;gap:4px;
  padding:5px 10px;border-radius:7px;
  border:1px solid ${C.border};background:white;
  color:${C.muted};font-size:10px;font-weight:600;
  cursor:pointer;font-family:inherit;transition:all .15s;
}
.nv-act:hover{border-color:#D8DBE6;color:${C.navy};}
.nv-act:disabled{opacity:.3;cursor:not-allowed;}
.nv-act-on{background:rgba(126,155,201,.06);border-color:${C.blue};color:${C.navy};}
.nv-act-badge{
  display:inline-flex;align-items:center;justify-content:center;
  min-width:16px;height:16px;border-radius:8px;
  background:${C.blue};color:white;font-size:9px;font-weight:700;
  padding:0 4px;
}

/* ── Error ── */
.nv-err{
  flex-shrink:0;display:flex;align-items:center;gap:6px;
  padding:8px 12px;border-radius:10px;
  background:rgba(199,84,84,.04);border:1px solid rgba(199,84,84,.12);
  color:#C75454;font-size:11px;font-weight:600;
}

/* ── Workspace: feed + optional log ── */
.nv-workspace{flex:1;display:flex;gap:8px;min-height:0;overflow:hidden;}
.nv-feed{
  flex:1;overflow-y:auto;overflow-x:hidden;
  display:flex;flex-direction:column;gap:4px;
  padding-right:2px;
  scrollbar-width:thin;scrollbar-color:${C.border} transparent;
}
.nv-empty{
  flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;
  min-height:200px;
}
.nv-empty-t{font-size:14px;font-weight:700;color:${C.ltGray};}
.nv-mute{font-size:11px;color:${C.hint};font-weight:500;}

/* ── Queue row cards ── */
.nv-row{
  display:flex;align-items:center;gap:8px;
  padding:8px 12px;border-radius:10px;
  background:white;border:1px solid rgba(34,45,80,.04);
  transition:all .15s;flex-shrink:0;
}
.nv-row:hover{border-color:rgba(126,155,201,.12);box-shadow:0 1px 6px rgba(34,45,80,.03);}
.nv-row-active{
  border-color:rgba(126,155,201,.2);
  box-shadow:0 0 0 2px rgba(126,155,201,.06);
}
.nv-row-n{
  width:22px;height:22px;border-radius:6px;flex-shrink:0;
  background:${C.bg};display:flex;align-items:center;justify-content:center;
  font-size:10px;font-weight:800;color:${C.muted};
}
.nv-row-cad{
  font-size:10px;font-weight:800;text-transform:uppercase;
  color:${C.navy};padding:2px 8px;border-radius:5px;
  background:rgba(126,155,201,.06);flex-shrink:0;
}
.nv-row-acct{
  flex:1;font-size:12px;font-weight:500;color:${C.gray};
  font-family:'Courier New',monospace;letter-spacing:.3px;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
}
.nv-row-sts{
  display:inline-flex;align-items:center;gap:4px;flex-shrink:0;
  font-size:10px;font-weight:700;padding:3px 9px;border-radius:6px;
}
.nv-row-file{
  font-size:9px;color:${C.hint};max-width:120px;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex-shrink:0;
}
.nv-row-rm{
  width:22px;height:22px;border-radius:5px;border:none;flex-shrink:0;
  background:transparent;color:${C.ltGray};
  display:flex;align-items:center;justify-content:center;
  cursor:pointer;transition:all .15s;
}
.nv-row-rm:hover{background:rgba(199,84,84,.06);color:#C75454;}
.nv-row-rm:disabled{opacity:.2;cursor:not-allowed;}

/* ── Log panel (slides in from right) ── */
.nv-log-panel{
  flex-shrink:0;display:flex;flex-direction:column;
  background:white;border-radius:10px;border:1px solid ${C.border};
  overflow:hidden;min-width:0;
}
.nv-log-hdr{
  flex-shrink:0;display:flex;align-items:center;gap:6px;
  padding:8px 10px;border-bottom:1px solid ${C.border};
  font-size:11px;font-weight:700;color:${C.navy};
}
.nv-log-box{
  flex:1;overflow-y:auto;padding:8px 10px;
  font-family:'Courier New',monospace;font-size:10px;
  line-height:1.7;color:${C.gray};
  scrollbar-width:thin;scrollbar-color:${C.border} transparent;
}
.nv-log-ln{white-space:pre-wrap;}

/* ── Utility ── */
.nv-spin{animation:nvspin 1s linear infinite;}
@keyframes nvspin{to{transform:rotate(360deg)}}

/* ── Scrollbars ── */
::-webkit-scrollbar{width:4px;height:4px;}
::-webkit-scrollbar-track{background:transparent;}
::-webkit-scrollbar-thumb{background:rgba(34,45,80,.08);border-radius:4px;}

/* ── Responsive ── */
@media(max-width:860px){
  .nv-body{grid-template-columns:1fr;}
  .nv-rail{
    flex-direction:row;flex-wrap:wrap;gap:8px;
    border-right:none;border-bottom:1px solid ${C.border};
    max-height:180px;overflow-y:auto;
    padding:10px 12px;
  }
  .nv-sec{margin-bottom:0;flex:1;min-width:200px;}
  .nv-sec-grow{flex:2;}
  .nv-add-stack{flex-direction:row;flex-wrap:wrap;}
  .nv-in{width:auto;flex:1;min-width:100px;}
  .nv-add-btn{width:auto;padding:9px 16px;}
  .nv-import{flex-direction:row;}
}
@media(max-width:640px){
  .nv-hdr{padding:8px 12px;}
  .nv-brand{display:none;}
  .nv-sep{display:none;}
  .nv-stat-bar{flex-wrap:wrap;gap:10px;}
  .nv-pills{gap:8px;flex-wrap:wrap;}
  .nv-pill-div{display:none;}
  .nv-log-panel{display:none;}
}

/* ── Credential modal ── */
.nv-cred-overlay{
  position:fixed;inset:0;z-index:100;
  display:flex;align-items:center;justify-content:center;
  background:rgba(34,45,80,.35);backdrop-filter:blur(4px);
}
.nv-cred-modal{
  width:380px;max-width:92vw;
  background:${C.card};border-radius:16px;
  padding:28px 28px 22px;
  box-shadow:0 16px 48px rgba(34,45,80,.18),0 0 0 1px rgba(34,45,80,.06);
  display:flex;flex-direction:column;gap:14px;
}
.nv-cred-icon-wrap{
  width:40px;height:40px;border-radius:10px;
  background:rgba(126,155,201,.08);
  display:flex;align-items:center;justify-content:center;
}
.nv-cred-title{
  font-size:16px;font-weight:800;color:${C.navy};margin:0;
}
.nv-cred-sub{
  font-size:12px;font-weight:500;color:${C.gray};margin:0;line-height:1.5;
}
.nv-cred-fields{display:flex;flex-direction:column;gap:10px;}
.nv-cred-lbl{display:flex;flex-direction:column;gap:4px;}
.nv-cred-lbl span{font-size:10px;font-weight:700;color:${C.muted};text-transform:uppercase;letter-spacing:.5px;}
.nv-cred-check{
  display:flex;align-items:center;gap:6px;
  font-size:11px;font-weight:600;color:${C.gray};cursor:pointer;
}
.nv-cred-check input[type=checkbox]{
  width:14px;height:14px;accent-color:${C.blue};cursor:pointer;
}
.nv-cred-actions{display:flex;justify-content:flex-end;gap:8px;margin-top:4px;}
.nv-cred-cancel{
  padding:8px 16px;border-radius:8px;
  border:1px solid ${C.border};background:${C.bg};
  color:${C.gray};font-size:12px;font-weight:600;
  cursor:pointer;font-family:inherit;transition:all .15s;
}
.nv-cred-cancel:hover{border-color:#D8DBE6;color:${C.navy};}
.nv-cred-submit{
  display:inline-flex;align-items:center;gap:5px;
  padding:8px 18px;border-radius:8px;border:none;
  background:${C.navy};color:${C.offWhite};
  font-size:12px;font-weight:700;cursor:pointer;font-family:inherit;
  transition:all .15s;
}
.nv-cred-submit:hover{background:${C.slate};}
.nv-cred-submit:disabled{opacity:.35;cursor:not-allowed;}
`;
